import type { JSX } from 'react';

/**
 * EXCALIBUR — heraldic sigil set.
 *
 * Art direction: illuminated medieval manuscript. Iron-gall ink line, gold leaf,
 * enamel blue / vermilion / verdigris. Every sigil is authored as the INNER content
 * of an <svg viewBox="0 0 100 100"> — the consumer supplies the <svg> wrapper.
 *
 * Rules obeyed here:
 *  - no external assets, no inline `style=` attributes (CSP), colours only via the
 *    eight CSS custom properties declared in cards.css
 *  - silhouette-first: each charge must be tellable apart at 56px
 *  - 4–14 drawing elements per sigil so a 62-chip board stays cheap to paint
 */

export type ChipId =
  | 'arthur' | 'artisan' | 'baroness' | 'blackKnight' | 'caelia' | 'cursedBlade'
  | 'excalibur' | 'fisherKing' | 'galahad' | 'gawain' | 'greenKnight' | 'guinevere'
  | 'ladyOfTheLake' | 'lancelot' | 'merchant' | 'merlin' | 'mordred' | 'morgana'
  | 'peasant' | 'percival' | 'priest' | 'robyn' | 'spirit' | 'spy'
  | 'squireSword' | 'thief' | 'uther';

/** Each value is the INNER content of an <svg viewBox="0 0 100 100">. No <svg> wrapper. */
export const SIGILS: Record<ChipId, JSX.Element> = {
  // ── ARTHUR ── an upright sword passing through a royal crown; the once and future king.
  arthur: (
    <>
      <path
        d="M50 6 L57 26 V52 H43 V26 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M50 12 V48" stroke="var(--gold-dim)" strokeWidth="1.4" strokeLinecap="round" />
      <path
        d="M24 64 V42 L34 52 L42 38 L50 48 L58 38 L66 52 L76 42 V64 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M24 58 H76" stroke="var(--ink)" strokeWidth="1.8" />
      <circle cx="34" cy="52" r="2.6" fill="var(--vermilion)" />
      <circle cx="66" cy="52" r="2.6" fill="var(--vermilion)" />
      <circle cx="50" cy="47" r="3.2" fill="var(--azure)" />
      <rect x="46" y="64" width="8" height="18" fill="var(--ink)" />
      <circle cx="50" cy="86" r="6" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.4" />
    </>
  ),

  // ── ARTISAN ── a smith's hammer struck over an anvil; the maker of things.
  artisan: (
    <>
      <path
        d="M18 46 H70 L86 39 V51 L70 58 H58 V66 L72 80 H24 L38 66 V58 H18 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M18 46 H70 L86 39 V44 L70 51 H18 Z" fill="var(--gold-dim)" />
      <path d="M28 48 L58 29" stroke="var(--ink)" strokeWidth="5.5" strokeLinecap="round" />
      <path
        d="M72.5 28.4 L63.2 34.3 L51.5 15.6 L60.8 9.7 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M40 24 L44 17" stroke="var(--vermilion)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M31 31 L36 27" stroke="var(--vermilion)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M24 84 H72" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  // ── BARONESS ── a pearled coronet set above a heraldic rose; the noble lady.
  baroness: (
    <>
      <path
        d="M50 50 A9 9 0 0 1 61.4 58.3 A9 9 0 0 1 57 71.7 A9 9 0 0 1 43 71.7 A9 9 0 0 1 38.6 58.3 A9 9 0 0 1 50 50 Z"
        fill="var(--vermilion)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <circle cx="50" cy="62" r="6" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" />
      <circle cx="50" cy="62" r="2" fill="var(--ink)" />
      <rect
        x="26"
        y="26"
        width="48"
        height="10"
        rx="2.5"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="2.4"
      />
      <circle cx="31" cy="21" r="4.6" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="2" />
      <circle cx="40.5" cy="17.5" r="4.6" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="2" />
      <circle cx="50" cy="16" r="4.6" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="2" />
      <circle cx="59.5" cy="17.5" r="4.6" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="2" />
      <circle cx="69" cy="21" r="4.6" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="2" />
      <path d="M30 40 H70" stroke="var(--gold-dim)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  // ── BLACK KNIGHT ── a closed great-helm, visor down, no device; anonymous menace.
  blackKnight: (
    <>
      <path d="M50 4 L44 18 H56 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <path
        d="M26 30 C26 16 38 10 50 10 C62 10 74 16 74 30 V60 C74 78 62 90 50 92 C38 90 26 78 26 60 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <rect x="29" y="38" width="42" height="6" rx="1.5" fill="var(--vellum-2)" />
      <rect x="47" y="30" width="6" height="46" fill="var(--shadow)" />
      <circle cx="41" cy="60" r="2.4" fill="var(--vellum-2)" />
      <circle cx="41" cy="70" r="2.4" fill="var(--vellum-2)" />
      <circle cx="59" cy="60" r="2.4" fill="var(--vellum-2)" />
      <circle cx="59" cy="70" r="2.4" fill="var(--vellum-2)" />
      <circle cx="31" cy="32" r="2.4" fill="var(--gold-dim)" />
      <circle cx="69" cy="32" r="2.4" fill="var(--gold-dim)" />
      <path d="M31 41 H41" stroke="var(--vermilion)" strokeWidth="2.6" strokeLinecap="round" />
    </>
  ),

  // ── CAELIA ── a circlet of thorns wreathed in briar around a fae star; the queen of Faerie.
  caelia: (
    <>
      <path
        d="M50 12 L59.2 29.8 L78.3 23.7 L72.2 42.8 L90 52 L72.2 61.2 L78.3 80.3 L59.2 74.2 L50 92 L40.8 74.2 L21.7 80.3 L27.8 61.2 L10 52 L27.8 42.8 L21.7 23.7 L40.8 29.8 Z"
        fill="none"
        stroke="var(--verd)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="50" cy="52" r="25" fill="none" stroke="var(--verd)" strokeWidth="4.5" />
      <circle cx="50" cy="52" r="16" fill="var(--vellum-2)" stroke="var(--ink)" strokeWidth="2" />
      <path
        d="M50 38 L54 48 L64 52 L54 56 L50 66 L46 56 L36 52 L46 48 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M28 32 Q34 24 42 28 Q36 36 28 32 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="1.8" />
      <path d="M72 72 Q66 80 58 76 Q64 68 72 72 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="1.8" />
      <circle cx="76" cy="34" r="3.2" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.4" />
      <circle cx="24" cy="70" r="3.2" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.4" />
    </>
  ),

  // ── CURSED BLADE ── a shattered black sword bleeding shadow, a serpent at its throat; doom.
  cursedBlade: (
    <>
      <path d="M50 84 Q34 89 29 98 H71 Q66 89 50 84 Z" fill="var(--vermilion)" />
      <path d="M50 2 L57 10 L50 18 L43 10 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="1.8" strokeLinejoin="round" />
      <rect x="46" y="16" width="8" height="10" fill="var(--ink)" />
      <path
        d="M30 24 L70 24 L67 30 Q59 35 50 35 Q41 35 33 30 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M42 33 H58 V50 L54 59 L50 48 L46 58 L42 48 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M52 68 L56 60 L62 68 L67 64 L66 82 L59 95 L52 80 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M46 58 V66 M53 60 V67" stroke="var(--vermilion)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M22 36 C6 52 12 78 30 88" fill="none" stroke="var(--vermilion)" strokeWidth="4" strokeLinecap="round" />
      <path d="M18 30 L32 34 L28 43 L14 39 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="23" cy="35" r="1.8" fill="var(--ink)" />
      <path
        d="M78 40 C92 56 88 78 72 90"
        fill="none"
        stroke="var(--ink)"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeDasharray="8 6"
      />
    </>
  ),

  // ── EXCALIBUR ── the sword in glory, blazing from a sixteen-point sun; the prize.
  excalibur: (
    <>
      <path
        d="M50 4 L54.3 28.4 L67.6 7.5 L62.2 31.7 L82.5 17.5 L68.3 37.8 L92.5 32.4 L71.6 45.7 L96 50 L71.6 54.3 L92.5 67.6 L68.3 62.2 L82.5 82.5 L62.2 68.3 L67.6 92.5 L54.3 71.6 L50 96 L45.7 71.6 L32.4 92.5 L37.8 68.3 L17.5 82.5 L31.7 62.2 L7.5 67.6 L28.4 54.3 L4 50 L28.4 45.7 L7.5 32.4 L31.7 37.8 L17.5 17.5 L37.8 31.7 L32.4 7.5 L45.7 28.4 Z"
        fill="var(--gold-dim)"
      />
      <circle cx="50" cy="50" r="20" fill="var(--gold)" stroke="var(--gold-dim)" strokeWidth="2" />
      <path
        d="M50 5 L57 25 V56 H43 V25 Z"
        fill="var(--vellum)"
        stroke="var(--ink)"
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <path d="M50 12 V52" stroke="var(--azure)" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M24 56 H76 L67 67 H33 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <rect x="46" y="67" width="8" height="17" fill="var(--ink)" />
      <circle cx="50" cy="89" r="6.5" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.6" />
      <circle cx="33" cy="61" r="2.6" fill="var(--vermilion)" />
      <circle cx="67" cy="61" r="2.6" fill="var(--vermilion)" />
    </>
  ),

  // ── FISHER KING ── a fish arched above the grail over still water; the wounded lord.
  fisherKing: (
    <>
      <path d="M34 46 H66 Q66 66 50 71 Q34 66 34 46 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.4" strokeLinejoin="round" />
      <rect x="47" y="71" width="6" height="9" fill="var(--gold)" stroke="var(--ink)" strokeWidth="1.8" />
      <path d="M38 88 H62 Q62 82 55 80 H45 Q38 82 38 88 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M34 52 H66" stroke="var(--ink)" strokeWidth="1.8" />
      <path
        d="M24 32 Q50 10 76 30 Q50 44 24 32 Z"
        fill="var(--azure)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M24 32 L11 22 L14 40 Z" fill="var(--azure)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="67" cy="29" r="2.2" fill="var(--vellum)" />
      <path d="M40 26 Q50 34 60 27" fill="none" stroke="var(--vellum-2)" strokeWidth="1.8" />
      <path d="M14 92 C24 86 30 96 40 90 C50 84 56 94 66 88 C76 82 82 92 90 88" fill="none" stroke="var(--azure)" strokeWidth="2.4" strokeLinecap="round" />
    </>
  ),

  // ── GALAHAD ── a fleur-de-lys lily haloed in gold; unstained purity.
  galahad: (
    <>
      <circle cx="50" cy="48" r="38" fill="none" stroke="var(--gold)" strokeWidth="2" />
      <path d="M50 4 V14 M84 24 L76 30 M16 24 L24 30 M92 62 L82 60 M8 62 L18 60" stroke="var(--gold)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M50 10 Q43 26 50 38 Q57 26 50 10 Z" fill="var(--azure)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path
        d="M50 38 Q35 22 27 34 Q21 46 40 51 Q47 52 50 45 Z"
        fill="var(--azure)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path
        d="M50 38 Q65 22 73 34 Q79 46 60 51 Q53 52 50 45 Z"
        fill="var(--azure)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <rect x="33" y="52" width="34" height="8" rx="2" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" />
      <path
        d="M50 60 Q44 76 30 82 Q42 86 50 76 Q58 86 70 82 Q56 76 50 60 Z"
        fill="var(--azure)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M50 60 V88" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" />
    </>
  ),

  // ── GAWAIN ── the endless pentangle on a blue shield; the sun knight's own device.
  gawain: (
    <>
      <path
        d="M18 14 H82 V50 Q82 78 50 94 Q18 78 18 50 Z"
        fill="var(--azure)"
        stroke="var(--gold)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <path
        d="M24 20 H76 V50 Q76 73 50 87 Q24 73 24 50 Z"
        fill="none"
        stroke="var(--gold-dim)"
        strokeWidth="1.4"
      />
      <path
        d="M50 28 L62.9 67.9 L29.1 43.2 L70.9 43.2 L37.1 67.9 Z"
        fill="none"
        stroke="var(--gold)"
        strokeWidth="3.4"
        strokeLinejoin="round"
      />
      <circle cx="50" cy="28" r="2.6" fill="var(--gold)" />
      <circle cx="70.9" cy="43.2" r="2.6" fill="var(--gold)" />
      <circle cx="62.9" cy="67.9" r="2.6" fill="var(--gold)" />
      <circle cx="37.1" cy="67.9" r="2.6" fill="var(--gold)" />
      <circle cx="29.1" cy="43.2" r="2.6" fill="var(--gold)" />
      <circle cx="50" cy="52" r="4.6" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.4" />
    </>
  ),

  // ── GREEN KNIGHT ── the severed head, holly-crowned, still smiling at the wound.
  greenKnight: (
    <>
      <ellipse cx="50" cy="60" rx="22" ry="26" fill="var(--verd)" stroke="var(--ink)" strokeWidth="2.4" />
      <path d="M38 56 Q43 51 48 56" fill="none" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M52 56 Q57 51 62 56" fill="none" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M42 70 Q50 75 58 70" fill="none" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M28 36 Q34 25 45 30 Q39 41 28 36 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M50 17 Q59 24 50 35 Q41 24 50 17 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M72 36 Q66 25 55 30 Q61 41 72 36 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="44" cy="35" r="2.8" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.2" />
      <circle cx="50" cy="38" r="2.8" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.2" />
      <circle cx="56" cy="35" r="2.8" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.2" />
      <path d="M31 82 Q50 91 69 82" fill="none" stroke="var(--vermilion)" strokeWidth="3.6" strokeLinecap="round" />
      <path d="M40 88 V95 M60 88 V95" stroke="var(--vermilion)" strokeWidth="2.6" strokeLinecap="round" />
    </>
  ),

  // ── GUINEVERE ── a crown above a swan on the water; the queen.
  guinevere: (
    <>
      <path
        d="M34 20 V8 L41 14 L50 4 L59 14 L66 8 V20 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <circle cx="50" cy="12" r="2.2" fill="var(--vermilion)" />
      <path
        d="M22 74 Q26 54 50 54 Q74 54 78 70 Q66 84 44 84 Q26 84 22 74 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M40 66 Q52 60 66 66" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
      <path d="M34 72 Q48 66 62 72" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
      <path
        d="M58 60 C40 54 38 34 50 28 C58 24 66 28 66 34 L61 34 C61 31 57 29 53 32 C45 38 47 52 60 56 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M66 32 L78 35 L66 38 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="62" cy="32" r="1.8" fill="var(--ink)" />
      <path d="M12 90 C22 84 30 94 40 88 C50 82 58 92 68 86 C78 80 84 90 92 86" fill="none" stroke="var(--azure)" strokeWidth="2.4" strokeLinecap="round" />
    </>
  ),

  // ── LADY OF THE LAKE ── an arm rising from the mere, offering the sword.
  ladyOfTheLake: (
    <>
      <path
        d="M0 74 C12 66 22 82 34 74 C46 66 56 82 68 74 C80 66 90 82 100 74 V100 H0 Z"
        fill="var(--azure)"
      />
      <path d="M6 84 C16 79 22 88 32 84" fill="none" stroke="var(--vellum-2)" strokeWidth="2" strokeLinecap="round" />
      <path d="M64 88 C74 83 82 92 94 87" fill="none" stroke="var(--vellum-2)" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M44 82 V54 Q44 48 50 48 Q56 48 56 54 V82 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M38 41 Q30 42 30 47 Q30 52 38 52 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path
        d="M38 38 Q38 32 45 32 H57 Q64 32 64 39 V49 Q64 56 56 56 H45 Q38 56 38 50 Z"
        fill="var(--vellum)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M40 38.5 H62 M39 44 H63 M40 49.5 H62" stroke="var(--ink)" strokeWidth="1.5" />
      <path
        d="M50 4 L55 17 V31 H45 V17 Z"
        fill="var(--vellum)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M35 31 H65" stroke="var(--gold)" strokeWidth="5" strokeLinecap="round" />
      <path d="M50 9 V29" stroke="var(--azure)" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),

  // ── LANCELOT ── a lance passed behind the shield of the first knight.
  lancelot: (
    <>
      <path d="M10 90 L84 18" stroke="var(--ink)" strokeWidth="4.4" strokeLinecap="round" />
      <path d="M80 22 L95 6 L89 24 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 92 L4 96 L8 84 Z" fill="var(--gold-dim)" stroke="var(--ink)" strokeWidth="1.8" strokeLinejoin="round" />
      <path
        d="M24 24 H76 V54 Q76 78 50 92 Q24 78 24 54 Z"
        fill="var(--vermilion)"
        stroke="var(--gold)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <path d="M30 30 L64 76" stroke="var(--vellum-2)" strokeWidth="8" strokeLinecap="round" />
      <path d="M30 30 L64 76" stroke="var(--gold-dim)" strokeWidth="1.4" />
      <path d="M43 29 L34 40" stroke="var(--vellum-2)" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M62 48 L54 60" stroke="var(--vellum-2)" strokeWidth="3.4" strokeLinecap="round" />
      <circle cx="42" cy="60" r="4" fill="var(--gold)" stroke="var(--ink)" strokeWidth="1.6" />
    </>
  ),

  // ── MERCHANT ── the balance, coin in one pan and purse in the other; commerce.
  merchant: (
    <>
      <path d="M50 30 V78" stroke="var(--ink)" strokeWidth="4.4" strokeLinecap="round" />
      <path d="M32 86 L40 76 H60 L68 86 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 30 H86" stroke="var(--ink)" strokeWidth="4.4" strokeLinecap="round" />
      <path d="M43 30 L50 17 L57 30 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M14 30 L2 46 M14 30 L26 46 M86 30 L74 46 M86 30 L98 46" stroke="var(--ink)" strokeWidth="1.8" />
      <path d="M2 46 Q14 60 26 46 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M74 46 Q86 60 98 46 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="14" cy="40" r="6" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" />
      <path d="M14 36 V44 M10 40 H18" stroke="var(--ink)" strokeWidth="1.4" />
      <path d="M79 38 Q86 32 93 38 Q97 45 86 47 Q75 45 79 38 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
    </>
  ),

  // ── MERLIN ── an owl beneath a falling comet; the wizard who reads the sky.
  merlin: (
    <>
      <path d="M58 26 Q40 30 24 44" fill="none" stroke="var(--gold-dim)" strokeWidth="3.2" strokeLinecap="round" />
      <path
        d="M76 6 L80 19 L93 23 L80 27 L76 40 L72 27 L59 23 L72 19 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M50 32 Q72 32 72 58 Q72 82 50 88 Q28 82 28 58 Q28 32 50 32 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M33 42 L26 24 L44 33 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M67 42 L74 24 L56 33 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="41" cy="52" r="7.5" fill="var(--gold)" />
      <circle cx="59" cy="52" r="7.5" fill="var(--gold)" />
      <circle cx="41" cy="52" r="3.2" fill="var(--ink)" />
      <circle cx="59" cy="52" r="3.2" fill="var(--ink)" />
      <path d="M50 57 L54 65 H46 Z" fill="var(--gold-dim)" stroke="var(--ink)" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M40 72 Q50 80 60 72" fill="none" stroke="var(--vellum-2)" strokeWidth="2" />
      <path d="M32 92 H68" stroke="var(--ink)" strokeWidth="3.2" strokeLinecap="round" />
      <path d="M16 68 L18 74 L24 76 L18 78 L16 84 L14 78 L8 76 L14 74 Z" fill="var(--azure)" />
    </>
  ),

  // ── MORDRED ── a serpent rearing over a usurped crown; the betrayer.
  mordred: (
    <>
      <path
        d="M32 76 V54 L41 63 L50 48 L59 63 L68 54 V76 Z"
        fill="var(--gold-dim)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <rect x="29" y="76" width="42" height="9" rx="2" fill="var(--ink)" />
      <circle cx="50" cy="80.5" r="2.4" fill="var(--vermilion)" />
      <path
        d="M18 88 C8 68 22 46 40 42 C56 38 64 28 58 20"
        fill="none"
        stroke="var(--verd)"
        strokeWidth="6.5"
        strokeLinecap="round"
      />
      <path
        d="M55 24 L74 10 L80 21 L63 32 Z"
        fill="var(--verd)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <circle cx="70" cy="19" r="2.2" fill="var(--vermilion)" />
      <path d="M80 18 L92 14 M80 21 L92 25" stroke="var(--vermilion)" strokeWidth="2" strokeLinecap="round" />
      <path d="M26 62 L34 66 M22 74 L31 76" stroke="var(--ink)" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),

  // ── MORGANA ── a raven perched in the horns of the waning moon; the sorceress.
  morgana: (
    <>
      <path
        d="M58 18 A34 34 0 1 0 58 82 A40 40 0 0 1 58 18 Z"
        fill="var(--gold)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M34 34 L36 40 L42 42 L36 44 L34 50 L32 44 L26 42 L32 40 Z" fill="var(--vermilion)" />
      <path
        d="M56 68 Q76 60 86 70 Q90 82 76 86 Q60 88 55 78 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <circle cx="57" cy="66" r="7" fill="var(--ink)" />
      <path d="M51 64 L38 67 L51 70 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="58" cy="64" r="1.7" fill="var(--gold)" />
      <path d="M86 76 L98 82 L85 85 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M64 72 Q74 70 80 76" fill="none" stroke="var(--vellum-2)" strokeWidth="1.6" />
      <circle cx="86" cy="24" r="2.6" fill="var(--gold-dim)" />
      <circle cx="76" cy="40" r="2" fill="var(--gold-dim)" />
    </>
  ),

  // ── PEASANT ── a bound sheaf of wheat with the sickle laid beside it; the common folk.
  peasant: (
    <>
      <path d="M50 88 C46 68 40 52 34 36" fill="none" stroke="var(--gold-dim)" strokeWidth="4" strokeLinecap="round" />
      <path d="M50 88 V34" stroke="var(--gold-dim)" strokeWidth="4" strokeLinecap="round" />
      <path d="M50 88 C54 68 60 52 66 36" fill="none" stroke="var(--gold-dim)" strokeWidth="4" strokeLinecap="round" />
      <path d="M34 22 Q43 30 34 41 Q25 30 34 22 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M50 15 Q60 26 50 39 Q40 26 50 15 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M66 22 Q75 30 66 41 Q57 30 66 22 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M34 27 V37 M50 21 V34 M66 27 V37" stroke="var(--ink)" strokeWidth="1.3" />
      <path d="M36 71 Q50 65 64 71 Q50 80 36 71 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M6 62 Q26 58 30 78" fill="none" stroke="var(--gold)" strokeWidth="5" strokeLinecap="round" />
      <path d="M6 62 L2 48" stroke="var(--ink)" strokeWidth="4" strokeLinecap="round" />
    </>
  ),

  // ── PERCIVAL ── the grail glimpsed through a chapel arch; the seeker at the threshold.
  percival: (
    <>
      <path
        d="M18 94 V44 Q18 16 50 4 Q82 16 82 44 V94 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M44 8 H56 V16 H44 Z" fill="var(--gold)" stroke="var(--ink)" strokeWidth="1.6" />
      <path
        d="M28 94 V46 Q28 26 50 16 Q72 26 72 46 V94 Z"
        fill="var(--ink)"
      />
      <path d="M40 44 H60 Q60 58 50 63 Q40 58 40 44 Z" fill="var(--gold)" stroke="var(--gold-dim)" strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="47" y="63" width="6" height="9" fill="var(--gold)" />
      <path d="M41 80 H59 Q59 74 53 72 H47 Q41 74 41 80 Z" fill="var(--gold)" strokeLinejoin="round" />
      <path d="M50 30 V38 M32 34 L38 40 M68 34 L62 40" stroke="var(--gold)" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M22 94 V50 M78 94 V50" stroke="var(--gold-dim)" strokeWidth="1.6" />
    </>
  ),

  // ── PRIEST ── a bishop's mitre charged with a cross; the church.
  priest: (
    <>
      <path d="M36 72 V90 L40 85 L44 90 V72 Z" fill="var(--vellum-2)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M56 72 V90 L60 85 L64 90 V72 Z" fill="var(--vellum-2)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path
        d="M50 6 C40 26 32 44 30 62 H70 C68 44 60 26 50 6 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M50 22 V52 M39 36 H61" stroke="var(--gold)" strokeWidth="5.5" strokeLinecap="square" />
      <path d="M50 22 V52 M39 36 H61" stroke="var(--ink)" strokeWidth="1.2" />
      <rect x="28" y="62" width="44" height="11" rx="2.5" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2.4" />
      <circle cx="38" cy="67.5" r="2.2" fill="var(--vermilion)" />
      <circle cx="50" cy="67.5" r="2.2" fill="var(--azure)" />
      <circle cx="62" cy="67.5" r="2.2" fill="var(--vermilion)" />
    </>
  ),

  // ── ROBYN ── the longbow drawn, arrow nocked, a green feather in the cap.
  robyn: (
    <>
      <path d="M32 8 Q68 50 32 92" fill="none" stroke="var(--ink)" strokeWidth="4.6" strokeLinecap="round" />
      <path d="M32 8 V92" stroke="var(--ink)" strokeWidth="1.8" />
      <path d="M14 50 H76" stroke="var(--ink)" strokeWidth="3.2" strokeLinecap="round" />
      <path d="M76 42 L94 50 L76 58 Z" fill="var(--ink)" stroke="var(--ink)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M16 50 L6 41 L12 50 L6 59 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M60 22 Q76 6 90 8 Q82 22 62 28 Z" fill="var(--verd)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M62 27 L88 9" stroke="var(--ink)" strokeWidth="1.4" />
      <path d="M36 16 Q46 22 36 28" fill="none" stroke="var(--gold-dim)" strokeWidth="2" />
      <path d="M36 72 Q46 78 36 84" fill="none" stroke="var(--gold-dim)" strokeWidth="2" />
    </>
  ),

  // ── SPIRIT ── a hooded wraith with a hollow face, fraying into mist.
  spirit: (
    <>
      <path
        d="M50 6 Q76 6 78 38 V62 Q78 76 70 88 Q63 78 56 88 Q49 78 42 88 Q35 78 28 88 Q22 76 22 62 V38 Q24 6 50 6 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeDasharray="7 4"
      />
      <path
        d="M36 24 Q50 14 64 24 Q64 46 50 52 Q36 46 36 24 Z"
        fill="var(--ink)"
      />
      <circle cx="43.5" cy="31" r="4.2" fill="var(--vermilion)" />
      <circle cx="56.5" cy="31" r="4.2" fill="var(--vermilion)" />
      <path d="M30 40 Q24 52 30 64" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
      <path d="M70 40 Q76 52 70 64" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
      <path d="M12 62 Q20 68 12 76" fill="none" stroke="var(--fog)" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M88 58 Q80 66 88 74" fill="none" stroke="var(--fog)" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M40 62 Q50 68 60 62" fill="none" stroke="var(--ink)" strokeWidth="1.8" />
    </>
  ),

  // ── SPY ── a watching eye set in a keyhole; nothing passes unseen.
  spy: (
    <>
      <path
        d="M50 10 C33 10 24 23 24 34 C24 43 28 49 33 53 L24 88 H76 L67 53 C72 49 76 43 76 34 C76 23 67 10 50 10 Z"
        fill="var(--ink)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M32 33 Q50 20 68 33 Q50 46 32 33 Z" fill="var(--vellum)" stroke="var(--vellum-2)" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="50" cy="33" r="7.5" fill="var(--azure)" />
      <circle cx="50" cy="33" r="3.2" fill="var(--ink)" />
      <circle cx="47.4" cy="30.4" r="1.7" fill="var(--vellum)" />
      <path d="M32 33 Q50 20 68 33" fill="none" stroke="var(--gold)" strokeWidth="1.8" />
      <path d="M44 62 L50 56 L56 62 L52 82 H48 Z" fill="var(--gold-dim)" />
      <path d="M30 84 H70" stroke="var(--gold-dim)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  // ── SQUIRE'S SWORD ── a plain arming sword, honest steel, no gold; the humble blade.
  squireSword: (
    <>
      <path
        d="M50 8 L56 26 V64 H44 V26 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <path d="M50 14 V60" stroke="var(--ink-soft)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M28 64 H72" stroke="var(--ink)" strokeWidth="6.5" strokeLinecap="round" />
      <rect x="46" y="68" width="8" height="16" fill="var(--ink)" />
      <path d="M46 72 H54 M46 76 H54 M46 80 H54" stroke="var(--vellum-2)" strokeWidth="1.2" />
      <circle cx="50" cy="88" r="5.5" fill="var(--vellum-2)" stroke="var(--ink)" strokeWidth="2.6" />
      <path d="M40 64 H60" stroke="var(--ink-soft)" strokeWidth="1.4" />
    </>
  ),

  // ── THIEF ── a hand lifting the last coin from a cut purse.
  thief: (
    <>
      <path
        d="M28 64 Q50 52 72 64 Q80 80 66 90 Q50 94 34 90 Q20 80 28 64 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M32 64 Q50 58 68 64" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M34 62 L28 54 M66 62 L72 54" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="50" cy="78" r="8" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" />
      <path d="M50 73 V83 M45 78 H55" stroke="var(--ink)" strokeWidth="1.4" />
      <path
        d="M34 48 V32 C34 28 40 28 40 32 V24 C40 20 46 20 46 24 V22 C46 18 52 18 52 22 V26 C52 22 58 22 58 28 L58 42 C58 52 50 58 42 58 C36 58 34 54 34 48 Z"
        fill="var(--vellum-2)"
        stroke="var(--ink)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <circle cx="66" cy="26" r="7" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" />
      <path d="M62 22 L70 30 M70 22 L62 30" stroke="var(--ink)" strokeWidth="1.3" />
      <path d="M76 44 L86 40 M78 52 L88 52" stroke="var(--gold-dim)" strokeWidth="2" strokeLinecap="round" />
    </>
  ),

  // ── UTHER ── the head of the dragon; pendragon, the chief of dragons.
  uther: (
    <>
      <path d="M16 52 L6 46 L13 58 L4 64" fill="none" stroke="var(--verd)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M34 26 C30 14 22 8 12 6 C18 16 20 22 25 29 Z" fill="var(--gold-dim)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M44 24 C42 14 36 8 28 4 C32 14 34 20 37 27 Z" fill="var(--gold-dim)" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" />
      <path
        d="M16 52 C16 30 24 24 36 24 C52 24 68 32 86 38 L94 44 L86 50 L54 52 L62 68 C40 70 24 64 16 52 Z"
        fill="var(--verd)"
        stroke="var(--ink)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path d="M55 52 L59 60 L63 52 L67 58 L71 52 L75 57 L79 52 Z" fill="var(--vellum)" stroke="var(--ink)" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="40" cy="38" r="5" fill="var(--gold)" stroke="var(--ink)" strokeWidth="2" />
      <path d="M40 34 V42" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="85" cy="43" r="2.2" fill="var(--ink)" />
      <path d="M78 60 Q90 64 84 76 Q94 72 92 60 Q88 54 78 60 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M26 44 Q34 48 30 54" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
    </>
  ),
};
